import { type QBankStructureLevelItemWithLevel } from '@exam-management/qbs-provider';
import { randomUUID } from '@shared/utils';

import {
  type LearningObjectiveStatement,
  type QBankStructureDashboardData,
  type DashboardData,
  type ItemSetQuestionPropertiesData,
} from '../../types';

import { findParentIds } from './find-parent-ids';
import { pushHierarchy } from './push-hierarchy';

function sortQBankStructureLevelItems(data: DashboardData[]): DashboardData[] {
  const sortedData: DashboardData[] = [];

  const itemsByParentId = new Map<string | null, DashboardData[]>();
  for (const item of data) {
    const parentId = item.parentLevelItemId ?? null;
    const children = itemsByParentId.get(parentId);
    if (children) {
      children.push(item);
    } else {
      itemsByParentId.set(parentId, [item]);
    }
  }

  pushHierarchy(null, itemsByParentId, sortedData);

  return sortedData;
}

function getTotalQuestionByLevelItemId(
  itemSetQuestionPropertiesData:
    | ItemSetQuestionPropertiesData['questionProperties'][number]
    | undefined,
  difficultyCategoryId: string,
  calculationCategoryId: string,
) {
  if (!itemSetQuestionPropertiesData) return null;

  const totalQuestions = itemSetQuestionPropertiesData.tagGroups.find((tagGroup) => {
    const hasDifficultyTag = tagGroup.tags.some((tag) => tag.id === difficultyCategoryId);

    const hasCalculationTag = tagGroup.tags.some((tag) => tag.id === calculationCategoryId);
    return hasDifficultyTag && hasCalculationTag;
  });
  return totalQuestions?.count ?? null;
}

export function generateItemSetQuestionRows(
  itemSetQuestionPropertiesData: ItemSetQuestionPropertiesData[],
  difficultyMap: {
    hard: string;
    medium: string;
    easy: string;
  },
  calculationMap: {
    calc: string;
    nonCalc: string;
  },
): QBankStructureDashboardData[] {
  const itemSetQuestionsInLos = itemSetQuestionPropertiesData.flatMap((itemSetQuestion) =>
    itemSetQuestion.questionProperties.map((property, index) => {
      return {
        parentId: itemSetQuestion.locationId,
        size: itemSetQuestion.size,
        id: randomUUID(),
        levelName: 'item-set-question',
        level: 'item-set-question' as const,
        // REMARK: this index is not displayed in the UI, used to satisfy the requirement of order field in QBankStructureDashboardData type
        order: index,
        parentIds: [],
        itemSetQuestions: [],
        calculate: {
          hardQuestions: getTotalQuestionByLevelItemId(
            property,
            difficultyMap.hard,
            calculationMap.calc,
          ),
          mediumQuestions: getTotalQuestionByLevelItemId(
            property,
            difficultyMap.medium,
            calculationMap.calc,
          ),
          easyQuestions: getTotalQuestionByLevelItemId(
            property,
            difficultyMap.easy,
            calculationMap.calc,
          ),
        },
        nonCalculate: {
          hardQuestions: getTotalQuestionByLevelItemId(
            property,
            difficultyMap.hard,
            calculationMap.nonCalc,
          ),
          mediumQuestions: getTotalQuestionByLevelItemId(
            property,
            difficultyMap.medium,
            calculationMap.nonCalc,
          ),
          easyQuestions: getTotalQuestionByLevelItemId(
            property,
            difficultyMap.easy,
            calculationMap.nonCalc,
          ),
        },
        total: property.totalQuestions,
      };
    }),
  );

  return itemSetQuestionsInLos;
}

export function addItemSetQuestionsToQBankStructureTree(
  qBankStructureTree: QBankStructureDashboardData[],
  itemSetQuestionRows: QBankStructureDashboardData[],
): QBankStructureDashboardData[] {
  return qBankStructureTree.reduce<QBankStructureDashboardData[]>((acc, levelItem) => {
    return [
      ...acc,
      levelItem,
      ...itemSetQuestionRows
        .filter((itemSetQuestion) => itemSetQuestion.parentId === levelItem.id)
        .map((entry) => {
          return {
            ...entry,
            parentIds: [...levelItem.parentIds, levelItem.id],
          };
        }),
    ];
  }, []);
}

export function generateLosRows(los: LearningObjectiveStatement[]): QBankStructureDashboardData[] {
  return [...los]
    .sort((a, b) => new Date(a.createdAt).valueOf() - new Date(b.createdAt).valueOf())
    .map((los, index) => {
      return {
        id: los.id,
        levelName: los.tag?.name,
        // REMARK: this index is not displayed in the UI, used to satisfy the requirement of order field in QBankStructureDashboardData type
        order: index,
        level: 'los',
        parentId: los.qBankStructureLevelItemId,
        parentIds: [],
        itemSetQuestions: [],
      };
    });
}

export function generateLevelItemRows(
  qBankStructureLevelItems: QBankStructureLevelItemWithLevel[],
): QBankStructureDashboardData[] {
  const sortedQBankStructureLevelItems = sortQBankStructureLevelItems(qBankStructureLevelItems);

  const tree = sortedQBankStructureLevelItems.map((levelItem) => ({
    id: levelItem.id,
    levelName: levelItem.tag?.name ?? 'ไม่ระบุ',
    order: levelItem.order,
    parentId: levelItem.parentLevelItemId,
    level: levelItem.qBankStructureLevel.category,
    parentIds: findParentIds(qBankStructureLevelItems, levelItem.parentLevelItemId),
    itemSetQuestions: [],
  }));

  return tree;
}

export function addLosToQBankStructureTree(
  qBankStructureTree: QBankStructureDashboardData[],
  losMapByLocation: Record<string, QBankStructureDashboardData[]>,
) {
  const newQBankStructureTree = qBankStructureTree.reduce<QBankStructureDashboardData[]>(
    (acc, levelItem) => {
      const los = losMapByLocation[levelItem.id] ?? [];

      if (los.length === 0) {
        acc.push(levelItem);

        return acc;
      }

      const losItems = (losMapByLocation[levelItem.id] ?? []).map((los) => ({
        ...los,
        parentId: levelItem.id,
        parentIds: [...levelItem.parentIds, levelItem.id],
      }));

      return [...acc, levelItem, ...losItems];
    },
    [],
  );

  return newQBankStructureTree;
}
